import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type React from 'react'
import type {ReactNode} from 'react'
import {useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'

import {
  copilotDockedPanelClosedEvent,
  copilotDockedPanelHeadingID,
  copilotDockedPanelHeaderID,
  copilotDockedPanelID,
  copilotDockedPanelReadyEvent,
  copilotDockedPanelTitleID,
} from '@github-ui/copilot-chat-helpers/constants'
import {
  useCopilotSidePanelActions,
  useCopilotSidePanelFocusRefs,
  useIsCopilotSidePanelCompact,
  useIsCopilotSidePanelOpen,
} from './copilot-side-panel-context'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {COPILOT_SIDE_PANEL_CONTENT_ID} from './copilot-side-panel-content'
import {Panel} from '@github-ui/docked-side-panel/Panel'

interface CopilotChatSidePanelProps {
  isOpen: boolean
  onClose: () => void
  toggleSidesheetRef: React.RefObject<HTMLElement | null>
  portalTargetId?: string
  /**
   * When true, render the docked Panel inline (in place) instead of into a portal target.
   * The Dialog fallback in compact mode is unchanged.
   */
  disablePortal?: boolean
  /**
   * Optional render function for the docked Panel chrome. Receives the chat
   * body, conversation-title slot, and header-actions slot, and should return the wrapping panel
   * element. Use this when the host page needs to render its own panel chrome
   * (e.g. with a panel selector dropdown). Only used in the non-compact path;
   * the Dialog fallback in compact mode is unchanged.
   */
  renderDockedPanel?: (args: {body: ReactNode; headerTitle: ReactNode; headerActions: ReactNode}) => ReactNode
  /**
   * Optional render function for the compact-mode body. When provided, the
   * built-in compact Dialog is bypassed and consumers can wrap the body in
   * their own shell (e.g. a host-managed shared compact Dialog).
   */
  renderCompactBody?: (args: {body: ReactNode; headerTitle: ReactNode; headerActions: ReactNode}) => ReactNode
  /** Replaces the static Copilot heading with the active conversation title supplied by ChatApp. */
  showConversationTitle?: boolean
}

export function CopilotChatSidePanel({
  isOpen,
  onClose,
  toggleSidesheetRef,
  portalTargetId,
  disablePortal,
  renderDockedPanel,
  renderCompactBody,
  showConversationTitle = false,
}: CopilotChatSidePanelProps) {
  const isPanelActive = useIsCopilotSidePanelOpen()
  const targetId = portalTargetId ?? COPILOT_SIDE_PANEL_CONTENT_ID
  const sidePanelContent = isPanelActive && !disablePortal ? ssrSafeDocument?.getElementById(targetId) : null
  const dialogCloseButtonRef = useRef<HTMLButtonElement>(null)
  const isCompact = useIsCopilotSidePanelCompact()
  const {closeButtonRef, returnFocusRef} = useCopilotSidePanelFocusRefs()
  const {closePanel} = useCopilotSidePanelActions()

  // Compact hosts may defer mounting the body until their portal slots attach.
  // Publish readiness from the target itself so consumers never cache a missing node.
  const dockedPanelTargetRef = useCallback((target: HTMLDivElement | null) => {
    if (target) {
      document.dispatchEvent(new CustomEvent(copilotDockedPanelReadyEvent))
    } else {
      queueMicrotask(() => {
        if (!document.getElementById(copilotDockedPanelID)) {
          document.dispatchEvent(new CustomEvent(copilotDockedPanelClosedEvent))
        }
      })
    }
  }, [])

  if (!isOpen) return null

  if (!isCompact && (sidePanelContent || disablePortal)) {
    const headerTitle = showConversationTitle ? (
      <span id={copilotDockedPanelTitleID} className="d-flex flex-1 min-width-0" />
    ) : (
      'Copilot'
    )
    const headerActions = <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
    const body = <div ref={dockedPanelTargetRef} id={copilotDockedPanelID} className="d-flex flex-column height-full" />
    const panel = renderDockedPanel ? (
      renderDockedPanel({body, headerTitle, headerActions})
    ) : (
      <Panel
        heading={headerTitle}
        headingId={showConversationTitle ? copilotDockedPanelHeadingID : undefined}
        headerActions={headerActions}
        closeButtonRef={closeButtonRef}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
      >
        <Panel.Body className="p-0">
          <Panel.ViewportFill bottomInsetPx={16}>{body}</Panel.ViewportFill>
        </Panel.Body>
      </Panel>
    )
    return sidePanelContent ? createPortal(panel, sidePanelContent) : panel
  }

  if (isCompact && renderCompactBody) {
    const headerTitle = showConversationTitle ? (
      <span id={copilotDockedPanelTitleID} className="d-flex flex-1 min-width-0" />
    ) : null
    const headerActions = <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
    const body = <div ref={dockedPanelTargetRef} id={copilotDockedPanelID} className="d-flex flex-column height-full" />
    return <>{renderCompactBody({body, headerTitle, headerActions})}</>
  }

  return (
    <Dialog
      initialFocusRef={dialogCloseButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>
              {showConversationTitle ? (
                <span id={copilotDockedPanelTitleID} className="d-flex flex-1 min-width-0" />
              ) : (
                'Copilot'
              )}
            </Dialog.Title>
            <div className="d-flex flex-items-center">
              <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
              <IconButton
                ref={dialogCloseButtonRef}
                type="button"
                aria-label="Close Copilot panel"
                icon={XIcon}
                variant="invisible"
                onClick={onClose}
              />
            </div>
          </div>
        </Dialog.Header>
      )}
    >
      <Panel.DialogFill>
        <div ref={dockedPanelTargetRef} id={copilotDockedPanelID} className="d-flex flex-column height-full" />
      </Panel.DialogFill>
    </Dialog>
  )
}
